import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ExpandMore,
  Chat,
  AudioFile,
  Headset,
  SupervisorAccount,
  PhoneIphone,
  Info,
  Speed,
  AttachMoney
} from '@mui/icons-material';

const interfaceDetails = {
  client_chat: {
    name: 'Client Chat',
    description: 'Real-time therapy sessions with clients',
    icon: <Chat />,
    priority: 'critical',
    color: '#dc2626'
  },
  therapist_review: {
    name: 'Therapist Review',
    description: 'Therapists listening to conversation recordings',
    icon: <Headset />,
    priority: 'high',
    color: '#f59e0b'
  },
  audio_export: {
    name: 'Audio Export',
    description: 'Batch audio generation for downloads',
    icon: <AudioFile />,
    priority: 'low',
    color: '#10b981'
  },
  supervisor_chat: {
    name: 'Supervisor Chat',
    description: 'Supervisor-therapist communication',
    icon: <SupervisorAccount />,
    priority: 'medium',
    color: '#3b82f6'
  },
  mobile_app: {
    name: 'Mobile App',
    description: 'Native mobile application',
    icon: <PhoneIphone />,
    priority: 'high',
    color: '#8b5cf6'
  }
};

const roleNames = {
  ai_therapist: 'AI Therapist',
  human_therapist: 'Human Therapist',
  client: 'Client',
  supervisor: 'Supervisor',
  system: 'System'
};

function InterfaceConfiguration({ configuration, providers, onConfigChange }) {
  if (!configuration) return null;

  const handleInterfaceChange = (interfaceName, field, value) => {
    const updatedConfig = {
      ...configuration,
      interfaceConfigs: {
        ...configuration.interfaceConfigs,
        [interfaceName]: {
          ...configuration.interfaceConfigs[interfaceName],
          [field]: value
        }
      }
    };
    onConfigChange(updatedConfig);
  };

  const handleFeatureToggle = (interfaceName, feature, value) => {
    const updatedConfig = {
      ...configuration,
      interfaceConfigs: {
        ...configuration.interfaceConfigs,
        [interfaceName]: {
          ...configuration.interfaceConfigs[interfaceName],
          features: {
            ...configuration.interfaceConfigs[interfaceName]?.features,
            [feature]: value
          }
        }
      }
    };
    onConfigChange(updatedConfig);
  };

  const handleRolloutChange = (interfaceName, field, value) => {
    const updatedConfig = {
      ...configuration,
      interfaceConfigs: {
        ...configuration.interfaceConfigs,
        [interfaceName]: {
          ...configuration.interfaceConfigs[interfaceName],
          rollout: {
            ...configuration.interfaceConfigs[interfaceName]?.rollout,
            [field]: value
          }
        }
      }
    };
    onConfigChange(updatedConfig);
  };

  const getProviderCost = (providerName) => {
    return configuration.providers?.[providerName]?.costPer1000Chars || 0;
  };

  const calculateMonthlySavings = (interfaceName, providerName) => {
    const elevenLabsCost = 0.18;
    const providerCost = getProviderCost(providerName);
    const charactersPerMonth = {
      client_chat: 8000000,  // 8M chars for real-time
      therapist_review: 2000000,  // 2M chars
      audio_export: 4000000,  // 4M chars for batch
      supervisor_chat: 1000000,  // 1M chars
      mobile_app: 3000000  // 3M chars
    };
    
    const chars = charactersPerMonth[interfaceName] || 1000000;
    const currentCost = (chars / 1000) * elevenLabsCost;
    const newCost = (chars / 1000) * providerCost;
    const savings = currentCost - newCost;
    
    return savings > 0 ? savings : 0;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Interface Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure TTS settings for each interface. Optimize for cost vs. quality based on use case.
      </Typography>

      {Object.entries(configuration.interfaceConfigs || {}).map(([interfaceName, interfaceConfig]) => {
        const details = interfaceDetails[interfaceName];
        const monthlySavings = calculateMonthlySavings(interfaceName, interfaceConfig.provider);
        
        return (
          <Accordion key={interfaceName} defaultExpanded={interfaceName === 'client_chat'}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                {details.icon}
                <Box flexGrow={1}>
                  <Typography variant="subtitle1">{details.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {details.description}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Chip 
                    label={details.priority} 
                    size="small"
                    sx={{ bgcolor: details.color, color: 'white' }}
                  />
                  {interfaceConfig.enabled && (
                    <Chip 
                      label={interfaceConfig.provider} 
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {monthlySavings > 0 && (
                    <Chip 
                      icon={<AttachMoney />}
                      label={`Save $${monthlySavings.toFixed(0)}/mo`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Basic Settings */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Basic Settings
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={interfaceConfig.enabled}
                            onChange={(e) => handleInterfaceChange(interfaceName, 'enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Enable TTS"
                      />
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Provider</InputLabel>
                        <Select
                          value={interfaceConfig.provider || 'elevenlabs'}
                          onChange={(e) => handleInterfaceChange(interfaceName, 'provider', e.target.value)}
                          label="Provider"
                        >
                          {providers.map(provider => (
                            <MenuItem key={provider.name} value={provider.name}>
                              <Box display="flex" justifyContent="space-between" width="100%">
                                <span>{provider.displayName}</span>
                                <Typography variant="caption" color="textSecondary">
                                  ${provider.costPer1000Chars}/1k
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={interfaceConfig.overrideRoleVoices || false}
                            onChange={(e) => handleInterfaceChange(interfaceName, 'overrideRoleVoices', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Override Role Voices"
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Features */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Features
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={interfaceConfig.features?.enableStreaming || false}
                            onChange={(e) => handleFeatureToggle(interfaceName, 'enableStreaming', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Enable Streaming"
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={interfaceConfig.features?.enableSSML || false}
                            onChange={(e) => handleFeatureToggle(interfaceName, 'enableSSML', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Enable SSML Enhancement"
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={interfaceConfig.features?.enableCaching || false}
                            onChange={(e) => handleFeatureToggle(interfaceName, 'enableCaching', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Enable Caching"
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Gradual Rollout */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Typography variant="subtitle2">
                          Gradual Rollout
                        </Typography>
                        <Tooltip title="Test new providers with a percentage of users before full deployment">
                          <IconButton size="small">
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={interfaceConfig.rollout?.enabled || false}
                            onChange={(e) => handleRolloutChange(interfaceName, 'enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Enable Gradual Rollout"
                      />
                      
                      {interfaceConfig.rollout?.enabled && (
                        <Box mt={2}>
                          <Typography gutterBottom>
                            Rollout Percentage: {interfaceConfig.rollout?.percentage || 0}%
                          </Typography>
                          <Slider
                            value={interfaceConfig.rollout?.percentage || 0}
                            onChange={(e, value) => handleRolloutChange(interfaceName, 'percentage', value)}
                            min={0}
                            max={100}
                            step={5}
                            marks
                            valueLabelDisplay="auto"
                          />
                          <Typography variant="caption" color="textSecondary">
                            {interfaceConfig.rollout?.percentage || 0}% of users will use {interfaceConfig.provider} 
                            {interfaceConfig.rollout?.percentage < 100 && `, ${100 - (interfaceConfig.rollout?.percentage || 0)}% will use ElevenLabs`}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Cost Summary */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cost Impact Summary
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Interface</TableCell>
                  <TableCell>Current Provider</TableCell>
                  <TableCell align="right">Est. Monthly Cost</TableCell>
                  <TableCell align="right">Potential Savings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(configuration.interfaceConfigs || {}).map(([interfaceName, config]) => {
                  const details = interfaceDetails[interfaceName];
                  const providerCost = getProviderCost(config.provider);
                  const chars = interfaceName === 'client_chat' ? 8000000 : 2000000;
                  const monthlyCost = (chars / 1000) * providerCost;
                  const savings = calculateMonthlySavings(interfaceName, config.provider);
                  
                  return (
                    <TableRow key={interfaceName}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {details.icon}
                          {details.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={config.provider} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        ${monthlyCost.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {savings > 0 ? (
                          <Chip 
                            label={`$${savings.toFixed(0)}`}
                            size="small"
                            color="success"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default InterfaceConfiguration;